import { Query } from '@apollo/client/react/components';
import { SiteUrls } from '@tableau/shared-urls';
import { IDataQualityIndicator } from '@tableau/vizportal-webclient-api/lib/api/types/IDataQualityIndicator';
import React from 'react';

import {
  PublishedDatasourceLineageUpstreamDataQualityWarningsQuery,
  PublishedDatasourceLineageUpstreamDataQualityWarningsQueryVariables,
  PublishedDatasourceLineageUpstreamDataQualityWarningsDocument
} from '../../../../../gen-src/queries';
import {
  AssetMarkedByIndicator,
  DataQualityDetailProps,
  VizPortalDQWDataCatalogProvider,
  WarningStatusChip,
  WarningStatusProps,
} from '../../../data-catalog-shared';
import { ErrorListProvider } from '../error/components/ErrorListProvider';
import { RenderQueryResult } from '../renderQueryResult';
import { emptyWarnings, filterOutWarningsFromAnchorUntilCycleIsFixed } from './DataQualityWarningCycleTemporaryFix';
import { EmbeddableDataQualityWarningProps } from './EmbeddableDataQualityWarningProps';

interface LineageWarningPublishedDatasourceProps {
  readonly asset: AssetMarkedByIndicator;
  readonly vizportalUrlId: string;
  readonly ownContentWarnings: ReadonlyArray<DataQualityDetailProps>;
  readonly anchoredWarnings: ReadonlyArray<IDataQualityIndicator>;
}

export type EmbeddablePublishedDatasourceDataQualityWarningProps =
  EmbeddableDataQualityWarningProps & LineageWarningPublishedDatasourceProps;

/** @deprecated TFS1478812 */
export function EmbeddablePublishedDatasourceDataQualityWarning(props: EmbeddablePublishedDatasourceDataQualityWarningProps): JSX.Element {
  const { source, siteUrls: urls } = props;

  return (
    <VizPortalDQWDataCatalogProvider {...props}>
      <ErrorListProvider>
        <Query<WarningQuery, WarningVariables>
          query={PublishedDatasourceLineageUpstreamDataQualityWarningsDocument}
          variables={{ vizportalUrlId: props.vizportalUrlId, first: 10, offset: 0 }}
        >
          {result => <RenderQueryResult result={result} renderers={{
            renderLoading: () => renderWarnings(urls, { ...props }, source),
            renderData: data => renderWarnings(urls, { ...props }, source, data),
          }} />}
        </Query>
      </ErrorListProvider>
    </VizPortalDQWDataCatalogProvider>
  );
}

type WarningQuery = PublishedDatasourceLineageUpstreamDataQualityWarningsQuery;
type WarningVariables = PublishedDatasourceLineageUpstreamDataQualityWarningsQueryVariables;

function renderWarnings(
  urls: SiteUrls,
  args: Pick<EmbeddablePublishedDatasourceDataQualityWarningProps, 'asset' | 'anchoredWarnings'>,
  source?: string,
  data?: WarningQuery,
): JSX.Element | null {
  const datasource = data && data.publishedDatasources ? data.publishedDatasources[0] : null;
  const anchoredWarnings = args.anchoredWarnings.filter(w => w.isActive);
  if (!datasource && anchoredWarnings.length < 1) {
    return null;
  }

  const upstreamWarnings = datasource
    ? filterOutWarningsFromAnchorUntilCycleIsFixed(datasource.id, datasource.upstreamDataQualityWarningsConnection?.nodes ?? emptyWarnings)
    : emptyWarnings;

  const props: WarningStatusProps = {
    subjectArea: 'PublishedDatasource',
    kind: 'vizportal',
    anchoredAsset: args.asset,
    iconOnly: false,
    anchoredWarnings,
    upstreamWarnings,
    urls,
    source: source ?? 'EmbeddablePublishedDatasourceDataQualityWarning',
  };

  return <WarningStatusChip {...props} />;
}
